/**
 * What waits on a secret's keepers, on the Secrets page (the workbench's 2026-11 secrets audit, slice 1):
 * changes to locked secrets held for a second keeper, requests from people for a secret their project cannot reach
 * (grant one for a while, say it is kept now, or refuse), and what the checks of agents' changes found by project.
 * Each group shows only when there is something in it.
 */

import { SECRET_SCOPE_TEXT, type SecretView } from '@aioe/shared';
import { useState } from 'react';

import { problemText, routes, type SecretChangeView, type SecretRequestView as SecretRequest } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import { FormField, FormSheet } from './ui/FormSheet.tsx';
import { RowMenu } from './ui/RowMenu.tsx';

const CHANGE_WORDS: Record<SecretChangeView['kind'], string> = { update: 'change', rotate: 'rotate', remove: 'remove', restore: 'put back an earlier value of' };

/** Changes to locked secrets that wait for a keeper other than the one who asked. */
export function SecretChangesPanel({ onDecided }: { onDecided?: () => void }) {
  const list = useLoad(() => routes.secretChanges('pending'), [], 60_000, { whenVisible: true });
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const decide = async (c: SecretChangeView, verb: 'approve' | 'refuse' | 'withdraw') => {
    setBusy(c.id);
    setError(undefined);
    try {
      await routes.decideSecretChange(c.id, verb);
      list.reload();
      onDecided?.();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const changes = list.data?.changes ?? [];
  if (!changes.length) return null;
  return (
    <ListGroup title="Changes waiting" count={changes.length} description="Your organisation holds each change to a locked secret until a second keeper approves it. Nobody approves their own.">
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {changes.map((c) => {
        const mine = c.mine === true;
        return (
          <ListRow
            key={c.id}
            title={
              <span>
                {mine ? 'You ask' : `${c.byName ?? 'A keeper'} asks`} to {CHANGE_WORDS[c.kind]} <span className="mono">{c.name ?? 'a secret'}</span>
              </span>
            }
            description={`${c.scope ? `${SECRET_SCOPE_TEXT[c.scope]} · ` : ''}asked ${timeAgo(c.at)}${c.kind === 'restore' && c.restoreVersion ? ` · version ${c.restoreVersion}` : ''}${c.kind === 'update' && c.facts ? ` · ${Object.keys(c.facts).join(', ')}` : ''}`}
            status={<StatusMark state="medium" label={mine ? 'Waiting for another keeper' : 'Waiting for you'} />}
            control={
              mine ? (
                <Button size="sm" variant="tertiary" isPending={busy === c.id} onPress={() => void decide(c, 'withdraw')}>
                  Withdraw
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="secondary" isPending={busy === c.id} onPress={() => void decide(c, 'approve')}>
                    Approve
                  </Button>
                  <RowMenu label={`More for the change to ${c.name ?? 'the secret'}`} items={[{ id: 'refuse', label: 'Refuse', danger: true, disabled: !!busy }]} onAction={() => void decide(c, 'refuse')} />
                </>
              )
            }
          />
        );
      })}
    </ListGroup>
  );
}

const DAYS: Record<string, string> = { '1': 'For a day', '7': 'For a week', '30': 'For 30 days', '90': 'For 90 days' };

/** People asking for a secret their project cannot reach, for the project's keepers to answer. */
export function SecretRequestsPanel({ onDecided }: { onDecided?: () => void }) {
  const list = useLoad(() => routes.secretRequests({ state: 'open' }), [], 60_000, { whenVisible: true });
  const [answering, setAnswering] = useState<SecretRequest>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const refuse = async (r: SecretRequest) => {
    setBusy(r.id);
    setError(undefined);
    try {
      await routes.decideSecretRequest(r.id, { answer: 'refuse' });
      list.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const requests = list.data?.requests ?? [];
  if (!requests.length) return null;
  return (
    <>
      <ListGroup title="Asked for" count={requests.length} description="Secrets people need on a project that cannot reach them. Grant one the project may use for a while, or refuse. Unanswered, a request lapses after 14 days.">
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
        {requests.map((r) => (
          <ListRow
            key={r.id}
            title={
              <span>
                {r.askedByName ?? <SubjectName id={r.askedBy} />} asks for <span className="mono">{r.name}</span>
              </span>
            }
            description={
              <>
                <span className="block">
                  {r.projectName ?? r.projectId}
                  {r.rootId ? `, repository ${r.rootId}` : ''} · asked {timeAgo(r.askedAt)}
                </span>
                {r.why ? <span className="block">“{r.why}”</span> : null}
              </>
            }
            control={
              <>
                <Button size="sm" variant="secondary" isDisabled={!!busy} onPress={() => setAnswering(r)}>
                  Answer
                </Button>
                <RowMenu label={`More for the request for ${r.name}`} items={[{ id: 'refuse', label: 'Refuse', danger: true, disabled: !!busy }]} onAction={() => void refuse(r)} />
              </>
            }
          />
        ))}
      </ListGroup>
      {answering ? (
        <AnswerRequest
          request={answering}
          onDone={() => {
            setAnswering(undefined);
            list.reload();
            onDecided?.();
          }}
          onCancel={() => setAnswering(undefined)}
        />
      ) : null}
    </>
  );
}

/** Grant one of the secrets of the name asked for, for a while; or say one the project reaches is kept now. */
function AnswerRequest({ request: r, onDone, onCancel }: { request: SecretRequest; onDone: () => void; onCancel: () => void }) {
  const kept = useLoad(() => routes.secrets(), []);
  const [secretId, setSecretId] = useState<string>();
  const [days, setDays] = useState('7');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  // Every shared secret of the name the person can see; the platform refuses one they do not keep.
  const candidates = (kept.data?.secrets ?? []).filter((s: SecretView) => s.name === r.name && s.scope !== 'personal');
  const chosen = candidates.find((s) => s.id === secretId) ?? candidates[0];
  const grantable = chosen && (chosen.scope === 'team' || chosen.scope === 'project' || chosen.scope === 'repository');
  const projects = useLoad(() => routes.projects().catch(() => []), []);
  const projectName = (id?: string) => projects.data?.find((p) => p.id === id)?.name ?? id;
  const where = (s: SecretView) => (s.scope === 'team' ? `Team ${s.teamId}` : s.scope === 'repository' ? `Repository ${s.rootId} of ${projectName(s.projectId)}` : s.scope === 'project' ? `Project ${projectName(s.projectId)}` : SECRET_SCOPE_TEXT[s.scope]);
  const send = async (answer: 'grant' | 'added') => {
    if (!chosen) return;
    setSaving(true);
    setError(undefined);
    try {
      await routes.decideSecretRequest(r.id, answer === 'grant' ? { answer, secretId: chosen.id, days: Number(days) } : { answer, secretId: chosen.id });
      onDone();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormSheet
      isOpen
      width="md"
      title={`Answer the request for ${r.name}`}
      description={`${r.askedByName ?? 'Someone'} needs it on ${r.projectName ?? `project ${r.projectId}`}${r.rootId ? `, repository ${r.rootId}` : ''}. A grant lets the project use a secret you keep until the date you choose; the person is told either way.`}
      onClose={onCancel}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={onCancel}>
            Cancel
          </Button>
          {chosen && !grantable ? (
            <Button size="sm" variant="primary" isPending={saving} onPress={() => void send('added')}>
              It is kept for them now
            </Button>
          ) : (
            <Button size="sm" variant="primary" isPending={saving} isDisabled={!chosen} onPress={() => void send('grant')}>
              Grant
            </Button>
          )}
        </>
      }
    >
      {!kept.data ? <div className="text-sm text-muted">Reading the secrets you keep…</div> : null}
      {kept.data && !candidates.length ? <p className="text-sm text-muted">You keep no secret called {r.name}. Add one where the project reaches it (on the project, or for a team it works with), then answer here; or refuse.</p> : null}
      {candidates.length > 1 ? (
        <FormField label="Which secret">
          <ChoiceSelect label="Which secret" className="w-full" value={chosen?.id ?? ''} options={Object.fromEntries(candidates.map((s) => [s.id, where(s)]))} onChange={setSecretId} />
        </FormField>
      ) : chosen ? (
        <p className="text-sm">
          <span className="mono">{chosen.name}</span> <span className="text-muted">· {where(chosen)}</span>
        </p>
      ) : null}
      {grantable ? (
        <FormField label="How long">
          <ChoiceSelect label="How long" className="w-48 max-w-full" value={days} options={DAYS} onChange={setDays} />
        </FormField>
      ) : chosen ? (
        <p className="text-sm text-muted">The organisation’s secrets already reach every project. If the person still cannot use it, it may be disabled or shadowed; say it is kept and they will ask again.</p>
      ) : null}
      <ErrorNote error={error} />
    </FormSheet>
  );
}

/** What the workbench's checks of agents' changes found, by project, over the last 30 days. */
export function SecretScansPanel() {
  const data = useLoad(() => routes.secretScans().catch(() => undefined), []);
  const projects = data.data?.projects ?? [];
  if (!projects.length) return null;
  return (
    <ListGroup title="Secrets found in agents’ changes" description="Before a change lands, the workbench checks it for anything that looks like a secret, and holds it back or warns as your rules say. The last 30 days.">
      {projects.map((p) => (
        <ListRow
          key={p.projectId}
          title={p.name ?? p.projectId}
          to={`/projects/${encodeURIComponent(p.projectId)}`}
          description={`${p.scans} ${p.scans === 1 ? 'change' : 'changes'} checked${p.lastAt ? `, last ${timeAgo(p.lastAt)}` : ''}${p.held ? ` · ${p.held} held back` : ''}${p.warned ? ` · ${p.warned} let through with a warning` : ''}`}
          status={p.findings ? <StatusMark state="medium" label={`${p.findings} ${p.findings === 1 ? 'finding' : 'findings'}`} /> : <StatusMark state="online" label="Nothing found" />}
        />
      ))}
    </ListGroup>
  );
}
